import isNil from 'lodash/isNil';
import {
  FilterType,
  IBasicFilterFieldsFragment,
  IConditionFieldsFragment,
  IFieldValue,
  ILogicalOperatorFieldsFragment,
  IPredicateFieldsFragment,
  ISimplePredicateFieldsFragment,
  OperandType,
} from '../../../utils/apollo/graphqlSchema';
import {
  castToFieldFragment,
  IFilterCondition,
  IFilterLogicOp,
  IFilterOperand,
  IFilterPredicate,
  IFilterWidgetState,
  IOperandValue,
  ISimpleFilterPredicate,
} from '../types';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { UnreachableCaseError } from 'ts-essentials';

const mapFieldValue = (value: IOperandValue): IFieldValue => {
  switch (value.__typename) {
    case 'AnyFieldValue': {
      return {
        __typename: value.__typename,
        name: value.name,
      };
    }
    case 'BasicFieldValue': {
      return {
        __typename: value.__typename,
        name: value.name,
        key: value.key,
        isDisabled: false,
        description: '',
      };
    }
    default:
      throw new UnreachableCaseError(value);
  }
};

const prepareOperands = (
  operand: IFilterOperand,
): ISimplePredicateFieldsFragment['operands'] => {
  if (operand.type === OperandType.Field) {
    const { fromField, toField, field } = operand;

    if (fromField && toField) {
      return {
        __typename: 'FieldOperands',
        fields: [castToFieldFragment(fromField), castToFieldFragment(toField)],
      };
    }

    assertNotNil(field);

    return {
      __typename: 'FieldOperands',
      fields: [castToFieldFragment(field)],
    };
  } else {
    const { operands, fromOperand, toOperand } = operand;

    if (fromOperand && toOperand) {
      return {
        __typename: 'ValueOperands',
        values: [fromOperand, toOperand].map(mapFieldValue),
      };
    }

    assertNotNil(operands);

    return {
      __typename: 'ValueOperands',
      values: operands.map(mapFieldValue),
    };
  }
};

const prepareSimplePredicate = (
  predicate: ISimpleFilterPredicate,
): ISimplePredicateFieldsFragment => {
  const { field, relOp, matchCase, operand, __typename } = predicate;

  assertCondition(!isNil(field));
  assertCondition(!isNil(relOp));

  return {
    __typename,
    field: castToFieldFragment(field),
    relOp,
    matchCase,
    operands: prepareOperands(operand),
  };
};

const preparePredicate = (
  predicate: IFilterPredicate,
): IPredicateFieldsFragment => {
  const { __typename } = predicate;
  if (__typename === 'SimplePredicate') {
    return prepareSimplePredicate(predicate);
  } else if (__typename === 'ArrayObjectPredicate') {
    return {
      __typename,
      elementPredicates: predicate.predicateElements.map(
        prepareSimplePredicate,
      ),
    };
  } else {
    throw new UnreachableCaseError(predicate);
  }
};

const prepareLogicOps = (
  logicOps: IFilterLogicOp[],
): ILogicalOperatorFieldsFragment[] => {
  if (!logicOps.length) {
    return [];
  }

  return logicOps.map((logicOp) => ({
    ...logicOp,
    __typename: 'LogicalOperator',
  }));
};

const prepareCondition = (
  condition: IFilterCondition,
): IConditionFieldsFragment => {
  return {
    __typename: 'Condition',
    logicOps: prepareLogicOps(condition.logicOps),
    predicates: condition.predicates.map(preparePredicate),
  };
};

const getFilterWidgetOutput = (
  basicFormData: IFilterWidgetState,
): IBasicFilterFieldsFragment => {
  assertCondition(basicFormData.type === FilterType.Basic);

  const { type, conditions, logicOps } = basicFormData;

  return {
    __typename: 'BasicFilter',
    type,
    conditions: conditions.map((condition) => prepareCondition(condition)),
    logicOps: prepareLogicOps(logicOps),
  };
};

export {
  mapFieldValue,
  prepareCondition,
  prepareLogicOps,
  prepareOperands,
  preparePredicate,
  getFilterWidgetOutput,
};
